import { useEffect, useReducer } from "react";

// Демо-режим — панель без подписки, на вымышленных данных.
//
// Включается с экрана «Подписка не активна» и с главной — тогда без аккаунта
// вовсе (гость), — и живёт в памяти вкладки.
// Пока он включён, apiFetch не ходит в сеть вовсе: на каждый запрос отвечает
// песочница из demo/demoApi.js. Поэтому ни одно действие не доходит до
// Kleinanzeigen, а нейросеть не вызывается и токены не тратятся.
//
// Сервер про демо ничего не знает и знать не должен: подписку проверяет он, и
// всё, что панель в демо «видит», существует только в этой вкладке.

const SESSION_KEY = "kl-demo-mode";
const GUEST_KEY = "kl-demo-guest";

// Свой владелец: всё, что панель хранит в браузере по ownerId (закрепы,
// пометки диалогов, кеш парсера), у демо отдельно от настоящего аккаунта.
export const DEMO_OWNER_ID = "demo-sandbox";
export const DEMO_USERNAME = "demo";

let active = false;
// Гость — демо открыто с главной, без входа: за подпиской ему сначала нужен
// аккаунт, а выход из демо ведёт обратно на главную.
let guest = false;
const assets = new Map();
// Номер сессии: песочница собирается заново, когда он меняется, поэтому
// каждый вход в демо начинается с чистых данных.
let sessionSeq = 0;
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

export const isDemoActive = () => active;
export const isDemoGuest = () => active && guest;
export const getDemoSession = () => sessionSeq;

export const enterDemoMode = ({ guest: asGuest = false } = {}) => {
  if (!active) {
    active = true;
    sessionSeq += 1;
  }
  guest = Boolean(asGuest);
  try {
    window.sessionStorage.setItem(SESSION_KEY, "1");
    if (guest) window.sessionStorage.setItem(GUEST_KEY, "1");
    else window.sessionStorage.removeItem(GUEST_KEY);
  } catch (_) { /* приватное окно */ }
  notify();
};

// Всё, что панель успела сложить в браузер от имени песочницы (кеш объявлений,
// пометки диалогов, состояние парсера), после выхода из демо не нужно.
const purgeDemoStorage = () => {
  [window.localStorage, window.sessionStorage].forEach((storage) => {
    try {
      Object.keys(storage)
        .filter((key) => key.includes(DEMO_OWNER_ID))
        .forEach((key) => storage.removeItem(key));
    } catch (_) {
      // Хранилище недоступно — чистить нечего.
    }
  });
};

export const leaveDemoMode = () => {
  active = false;
  guest = false;
  assets.clear();
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(GUEST_KEY);
  } catch (_) { /* приватное окно */ }
  purgeDemoStorage();
  notify();
};

// Перезагрузка страницы не должна выкидывать из демо: флаг лежит в
// sessionStorage и живёт ровно столько, сколько вкладка. Сам режим после
// перезагрузки включает панель — когда сервер подтвердил, что подписки нет.
export const wasDemoRequested = () => {
  try { return window.sessionStorage.getItem(SESSION_KEY) === "1"; } catch (_) { return false; }
};
export const wasGuestDemoRequested = () => {
  try { return wasDemoRequested() && window.sessionStorage.getItem(GUEST_KEY) === "1"; } catch (_) { return false; }
};

// Картинки, которые песочница «хранит на сервере» (результаты редактора фото).
// Панель просит их по id через /api/ai/photos/…, а тег <img> идёт мимо
// apiFetch — поэтому адрес отдаётся отсюда, синхронно, без песочницы.
export const registerDemoAsset = (id, url) => { if (id && url) assets.set(String(id), url); };
export const getDemoAsset = (id) => (active ? assets.get(String(id || "")) || "" : "");

export const useDemoMode = () => {
  const [, force] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => listeners.delete(force);
  }, []);
  return active;
};
